/**
 * Gingerlilly: cart drawer upsells (snippets/gl-cart-upsell-card.liquid).
 * Add adds straight to cart. Products with sizes first open a size picker over the
 * card image; choosing a size adds that variant. Uses document-level listeners because
 * the cards are replaced every time the cart drawer re-renders.
 */
if (!window.glCartUpsells) {
  window.glCartUpsells = true;

  const setChoosing = (card, open) => {
    const panel = card.querySelector('.js-gl-upsell-sizes');
    if (!panel) return;
    card.classList.toggle('is-choosing', open);
    panel.setAttribute('aria-hidden', String(!open));
    panel.querySelectorAll('button').forEach((btn) => { btn.tabIndex = open ? 0 : -1; });
    if (open) {
      const first = panel.querySelector('.js-gl-upsell-variant:not([disabled])');
      if (first) first.focus({ preventScroll: true });
    }
  };

  const closeAll = (except) => {
    document.querySelectorAll('.gl-upsell.is-choosing').forEach((card) => {
      if (card !== except) setChoosing(card, false);
    });
  };

  const addToCart = async (variantId, btn) => {
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-disabled', 'true');

    const sectionsToUpdate = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter((el) => el);
    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionsToUpdate.map((el) => el.dataset.sectionId).join(','),
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || response.status);

      if (data.sections && sectionsToUpdate.length === Object.keys(data.sections).length) {
        sectionsToUpdate.forEach((el) => el.updateFromCartChange(data.sections[el.dataset.sectionId]));
      } else {
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
      }
      document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: Number(variantId) } }));
    } catch (error) {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-disabled');
      const message = typeof error.message === 'string' && error.message ? error.message : 'Sorry, this could not be added.';
      if (theme.showQuickPopup) theme.showQuickPopup(message, btn);
    }
  };

  document.addEventListener('click', (evt) => {
    const addBtn = evt.target.closest('.js-gl-upsell-add');
    if (addBtn) {
      const card = addBtn.closest('.gl-upsell');
      if (addBtn.dataset.variantId) {
        closeAll();
        addToCart(addBtn.dataset.variantId, addBtn);
      } else {
        const open = !card.classList.contains('is-choosing');
        closeAll(card);
        setChoosing(card, open);
      }
      return;
    }

    const variantBtn = evt.target.closest('.js-gl-upsell-variant');
    if (variantBtn) {
      addToCart(variantBtn.dataset.variantId, variantBtn);
      return;
    }

    const closeBtn = evt.target.closest('.js-gl-upsell-close');
    if (closeBtn) {
      setChoosing(closeBtn.closest('.gl-upsell'), false);
      return;
    }

    // Clicking anywhere else closes an open size picker.
    if (!evt.target.closest('.gl-upsell__sizes')) closeAll();
  });
}

/**
 * Gingerlilly: cart drawer Recently viewed row. Reads the handles saved by layout/theme.liquid,
 * fetches each product's card (sections/gl-upsell-card.liquid) and leaves out anything in the cart.
 * Loads when the drawer opens; cards are cached for the page so cart updates re-render instantly.
 */
if (!customElements.get('gl-recently-viewed')) {
  const MAX_CARDS = 8;
  const cardCache = new Map();

  const getHandles = () => {
    try {
      return JSON.parse(localStorage.getItem('gl:recently-viewed') || '[]');
    } catch (e) {
      return [];
    }
  };

  const fetchCard = (handle) => {
    if (!cardCache.has(handle)) {
      const url = `${window.Shopify.routes.root}products/${encodeURIComponent(handle)}?section_id=gl-upsell-card`;
      cardCache.set(handle, fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          return tmp.querySelector('.gl-upsell');
        })
        .catch(() => null));
    }
    return cardCache.get(handle);
  };

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const drawer = this.closest('.drawer');
      if (!drawer || drawer.hasAttribute('open')) {
        this.load();
      } else {
        this.openHandler = () => this.load();
        document.addEventListener('on:cart-drawer:before-open', this.openHandler, { once: true });
      }
    }

    disconnectedCallback() {
      if (this.openHandler) document.removeEventListener('on:cart-drawer:before-open', this.openHandler);
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;

      const handles = getHandles();
      if (!handles.length) return;

      const cards = await Promise.all(handles.map(fetchCard));
      const inCart = this.dataset.cartIds || '';
      const visible = cards
        .filter((card) => card && !inCart.includes(`,${card.dataset.productId},`))
        .slice(0, MAX_CARDS);
      if (!visible.length || !this.isConnected) return;

      this.querySelector('.slider__grid').replaceChildren(...visible.map((card) => card.cloneNode(true)));
      this.closest('.gl-recently-viewed-wrap').hidden = false;
      const carousel = this.querySelector('carousel-slider');
      if (carousel && carousel.refresh) carousel.refresh();
    }
  }

  customElements.define('gl-recently-viewed', RecentlyViewed);
}

/**
 * Gingerlilly: applies the cart drawer settings to the recommended products once
 * cc-cart-cross-sell has loaded them: pins the gift box first (rendered by the cart drawer
 * into a <template> when it should show), caps the number of products and sets the Add text.
 */
if (!customElements.get('gl-cart-upsell-config')) {
  class CartUpsellConfig extends HTMLElement {
    connectedCallback() {
      this.crossSell = this.parentElement.querySelector('cc-cart-cross-sell');
      if (!this.crossSell) return;
      this.list = this.crossSell.querySelector('.product-grid');
      this.observer = new MutationObserver(() => this.apply());
      this.observe();
      this.apply();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    observe() {
      this.observer.observe(this.list, { childList: true });
      this.observer.observe(this.crossSell, { attributes: true, attributeFilter: ['class'] });
    }

    apply() {
      this.observer.disconnect();

      const template = this.querySelector('template');
      const pinned = template ? template.content.querySelector('.gl-upsell') : null;
      if (pinned) {
        this.list.querySelectorAll(`.gl-upsell[data-product-id="${pinned.dataset.productId}"]`).forEach((el) => el.remove());
        this.list.prepend(pinned.cloneNode(true));
        // No recommendations came back: show the gift box on its own
        this.crossSell.classList.remove('hidden');
      }

      const max = Number(this.dataset.max) || 8;
      [...this.list.querySelectorAll('.gl-upsell')].slice(max).forEach((el) => el.remove());

      const text = this.dataset.buttonText;
      if (text) {
        this.list.querySelectorAll('.gl-upsell__add:not(.is-loading)').forEach((btn) => { btn.textContent = text; });
      }

      // Prepending the gift box shifts the scroll position; start from the first card
      const slider = this.crossSell.querySelector('.slider');
      if (slider) slider.scrollLeft = 0;
      const carousel = this.crossSell.querySelector('carousel-slider');
      if (carousel && carousel.refresh) carousel.refresh();

      this.observe();
    }
  }

  customElements.define('gl-cart-upsell-config', CartUpsellConfig);
}
